import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import type { CurrentUser } from "@/lib/auth/types";
import { apiGet, apiPost } from "@/lib/client/api";

type AuthStatus = "loading" | "authenticated" | "anonymous";

type LoginInput = {
  email: string;
  password: string;
};

type AuthContextValue = {
  status: AuthStatus;
  user: CurrentUser | null;
  login: (input: LoginInput) => Promise<CurrentUser>;
  logout: () => Promise<void>;
  refresh: () => Promise<CurrentUser | null>;
};

type MeResponse = {
  user: CurrentUser | null;
};

type LoginResponse = {
  user: CurrentUser;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<CurrentUser | null>(null);

  const setSession = useCallback((nextUser: CurrentUser | null) => {
    setUser(nextUser);
    setStatus(nextUser ? "authenticated" : "anonymous");
  }, []);

  const refresh = useCallback(async () => {
    try {
      const response = await apiGet<MeResponse>("/api/me");
      setSession(response.user);
      return response.user;
    } catch {
      setSession(null);
      return null;
    }
  }, [setSession]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (input: LoginInput) => {
      const response = await apiPost<LoginInput, LoginResponse>("/api/auth/login", input);
      setSession(response.user);
      return response.user;
    },
    [setSession],
  );

  const logout = useCallback(async () => {
    try {
      await apiPost<Record<string, never>, { ok: true }>("/api/auth/logout", {});
    } finally {
      setSession(null);
    }
  }, [setSession]);

  const value = useMemo(
    () => ({
      status,
      user,
      login,
      logout,
      refresh,
    }),
    [login, logout, refresh, status, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);

  if (!value) {
    throw new Error("useAuth must be used inside AuthProvider");
  }

  return value;
}
